import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppReadiness } from 'src/auth/hooks/use-app-readiness';
import { SplashScreen } from 'src/components/loading-screen';
import { usePathname } from 'src/routes/hooks';
import type { RouteRequirements } from 'src/types/route-metadata';
import { storeOriginalPath } from 'src/utils/onboardingRedirect';

// ----------------------------------------------------------------------

interface PolicyGuardProps {
  children: React.ReactNode;
  requirements?: RouteRequirements;
  fallback?: React.ReactNode;
}

/**
 * Unified policy guard that replaces the nested guard pattern.
 *
 * This component:
 * - Uses the policy engine (useAppReadiness) to determine access
 * - Shows consistent loading UI
 * - Handles redirects declaratively
 * - Supports route-specific requirements
 *
 * Replaces the pattern of:
 * <AuthGuard>
 *   <CompanyContextGuard>
 *     <OnboardingRedirectGuard>
 *       {children}
 *     </OnboardingRedirectGuard>
 *   </CompanyContextGuard>
 * </AuthGuard>
 */
export default function PolicyGuard({ children, requirements, fallback }: PolicyGuardProps) {
  const readiness = useAppReadiness({ requirements });
  const pathname = usePathname();

  useEffect(() => {
    if (readiness.state === 'redirect' && readiness.reason === 'Onboarding required') {
      storeOriginalPath(pathname);
    }
  }, [pathname, readiness.reason, readiness.state]);

  // Show loading state
  if (readiness.state === 'loading') {
    return fallback || <SplashScreen />;
  }

  // Handle redirects
  if (readiness.state === 'redirect' && readiness.redirectTo) {
    return <Navigate to={readiness.redirectTo} replace />;
  }

  // Ready to render children
  return <>{children}</>;
}

// ----------------------------------------------------------------------

/**
 * Pre-configured guard for standard protected routes
 * (auth + company + onboarding + subscription + shopify permissions required)
 */
export function StandardPolicyGuard({
  children,
  fallback,
}: Omit<PolicyGuardProps, 'requirements'>) {
  return (
    <PolicyGuard
      requirements={{
        needsAuth: true,
        needsCompany: true,
        needsOnboardingComplete: true,
        needsValidSubscription: true,
        needsShopifyPermissions: true,
      }}
      fallback={fallback}
    >
      {children}
    </PolicyGuard>
  );
}

// ----------------------------------------------------------------------

/**
 * Pre-configured guard for auth-only routes
 */
export function AuthOnlyPolicyGuard({
  children,
  fallback,
}: Omit<PolicyGuardProps, 'requirements'>) {
  return (
    <PolicyGuard
      requirements={{
        needsAuth: true,
      }}
      fallback={fallback}
    >
      {children}
    </PolicyGuard>
  );
}

// ----------------------------------------------------------------------

/**
 * Pre-configured guard for billing page
 * Requires auth, company, and onboarding complete, but NOT a valid subscription
 * (since billing page is where users go to fix their subscription)
 */
export function BillingPolicyGuard({ children, fallback }: Omit<PolicyGuardProps, 'requirements'>) {
  return (
    <PolicyGuard
      requirements={{
        needsAuth: true,
        needsCompany: true,
        needsOnboardingComplete: true,
        needsValidSubscription: false,
        needsShopifyPermissions: false,
      }}
      fallback={fallback}
    >
      {children}
    </PolicyGuard>
  );
}
